import Cookies from 'js-cookie';
import fetchWrapper from './fetchWrapper';

const BASE = process.env.REACT_APP_TICKETING_API_URL || '/api/ticketing';
const DEFAULT_ISSUE_TYPE = process.env.REACT_APP_DEFAULT_ISSUE_TYPE || 'Question';

function qs(params) {
  const entries = Object.entries(params).filter(([, v]) => v != null && v !== '');
  if (entries.length === 0) return '';
  return '?' + new URLSearchParams(entries).toString();
}

function getAuthToken() {
  return Cookies.get('accessToken') || Cookies.get('jwtToken');
}

function postMultipart(url, formData, { onProgress } = {}) {
  const token = getAuthToken();
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && typeof onProgress === 'function') {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      const contentType = xhr.getResponseHeader('content-type');
      const isJson = contentType && contentType.includes('application/json');
      const json = isJson ? JSON.parse(xhr.responseText || '{}') : {};
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(json);
      } else {
        if (xhr.status === 401) {
          const baseUrl = process.env.REACT_APP_BASE_URL || '';
          if (baseUrl) window.open(`${baseUrl}/session-expired`, '_self');
        }
        reject({ status: xhr.status, json });
      }
    };

    xhr.onerror = () => reject({ status: 0, json: { error: 'Network error' } });
    xhr.send(formData);
  });
}

/**
 * Create ticket with optional files. When files are provided, uses XHR for upload progress.
 * @param {{ subject: string, description: string, issueType?: string, files?: File[] }}
 * @param {{ onProgress?: (percent: number) => void }}
 */
export async function createTicket(
  { subject, description, issueType = DEFAULT_ISSUE_TYPE, files = [] },
  { onProgress } = {}
) {
  const fileList = Array.isArray(files) ? files : [];
  if (fileList.length === 0) {
    const res = await fetchWrapper(`${BASE}/v1/tickets`, {
      method: 'POST',
      body: { subject, description, issueType },
    });
    return res.json;
  }

  const form = new FormData();
  form.append('subject', subject);
  form.append('description', description);
  form.append('issueType', issueType);
  fileList.forEach((f) => form.append('files', f));

  return postMultipart(`${BASE}/v1/tickets`, form, { onProgress });
}

/**
 * Attach files to an existing ticket.
 * @param {string} ticketId
 * @param {File[]} files
 * @param {{ onProgress?: (percent: number) => void }}
 */
export async function attachFiles(ticketId, files = [], { onProgress } = {}) {
  const fileList = Array.isArray(files) ? files.filter(Boolean) : [];
  if (fileList.length === 0) {
    return Promise.reject({ status: 400, json: { error: 'At least one file is required' } });
  }
  const form = new FormData();
  fileList.forEach((f) => form.append('files', f));
  return postMultipart(
    `${BASE}/v1/tickets/${encodeURIComponent(ticketId)}/attachments`,
    form,
    { onProgress }
  );
}

export async function listTickets({ page = 1, pageSize = 20, search, status, dateFrom, dateTo } = {}) {
  const params = { page, pageSize };
  if (search) params.search = search;
  if (status) params.status = Array.isArray(status) ? status.join(',') : status;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;
  const res = await fetchWrapper(`${BASE}/v1/tickets${qs(params)}`);
  return res.json;
}

export async function getTicket(ticketId) {
  const res = await fetchWrapper(`${BASE}/v1/tickets/${encodeURIComponent(ticketId)}`);
  return res.json;
}

export async function addComment(ticketId, body) {
  const res = await fetchWrapper(`${BASE}/v1/tickets/${encodeURIComponent(ticketId)}/comments`, {
    method: 'POST',
    body: { body },
  });
  return res.json;
}

export async function getWatchers(ticketId) {
  const res = await fetchWrapper(`${BASE}/v1/tickets/${encodeURIComponent(ticketId)}/watchers`);
  return res.json;
}

export async function addWatcher(ticketId, userId) {
  await fetchWrapper(`${BASE}/v1/tickets/${encodeURIComponent(ticketId)}/watchers`, {
    method: 'POST',
    body: { userId },
  });
}

export async function removeWatcher(ticketId, userId) {
  await fetchWrapper(`${BASE}/v1/tickets/${encodeURIComponent(ticketId)}/watchers/${userId}`, {
    method: 'DELETE',
  });
}

export async function searchOrgUsers(search) {
  const res = await fetchWrapper(`${BASE}/v1/users${qs({ search })}`);
  return res.json;
}
